import { ConfirmDialog } from '@/components/dialog/confirm-dialog';
import { Box, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { ShieldCheck } from 'iconoir-react';
import { useTranslation } from 'react-i18next';

interface StopBoostConfirmDialogProps {
  isOpen: boolean;
  isStopping: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  /**
   * The brand campaign the boost was placed in, when Fluenceur did not create
   * it. It changes what stopping means, so the copy says so explicitly: only
   * the Fluenceur ad set and ad are paused, the campaign is left alone.
   */
  parentCampaignName?: string | null;
}

/**
 * Confirmation step before stopping a running boost. The copy is honest: Meta
 * winds delivery down, so the ad may keep running briefly. Destructive red
 * confirm; cancel reuses the shared `common.cancel` default.
 */
export function StopBoostConfirmDialog({
  isOpen,
  isStopping,
  onConfirm,
  onCancel,
  parentCampaignName = null,
}: StopBoostConfirmDialogProps) {
  const { t } = useTranslation();

  return (
    <ConfirmDialog
      isOpen={isOpen}
      title={t('mentions.boost.stop.confirm.title')}
      description={
        parentCampaignName ? (
          <ExistingCampaignStopBody campaignName={parentCampaignName} />
        ) : (
          t('mentions.boost.stop.confirm.body')
        )
      }
      confirmText={t(
        parentCampaignName
          ? 'mentions.boost.stop.confirm.pauseCta'
          : 'mentions.boost.stop.confirm.confirmCta'
      )}
      confirmColorPalette="red"
      onConfirm={onConfirm}
      onCancel={onCancel}
      isLoading={isStopping}
    />
  );
}

/** Stop copy for a boost living inside a campaign the brand owns. */
function ExistingCampaignStopBody({ campaignName }: { campaignName: string }) {
  const { t } = useTranslation();

  return (
    <VStack align="stretch" gap={3}>
      <Text fontSize="sm" color="fg.muted">
        {t('mentions.boost.stop.confirm.existingCampaignBody', { campaign: campaignName })}
      </Text>
      <Box border="1px solid" borderColor="border.muted" bg="bg.subtle" rounded="md" px={3} py={2}>
        <HStack align="flex-start" gap={2}>
          <Icon size="xs" color="fg.muted" mt={0.5}>
            <ShieldCheck />
          </Icon>
          <Text fontSize="xs" color="fg.muted">
            {t('mentions.boost.stop.confirm.ownershipNote')}
          </Text>
        </HStack>
      </Box>
    </VStack>
  );
}
